import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../services/apiService";
import ReusableTable from "../../dashboard/ReusableTable";
import Select from "react-select";
import {
  LayoutDashboard,
  Calendar,
  BarChart3,
  Users,
  Folder,
  MessageSquare,
  HelpCircle,
  LogOut,
  Database,
  Settings,
  HardDrive,
  Layers,
  Building,
  FileText,
  Flag,
  Banknote,
  Package,
  FileBarChart,
  CalendarClock,
  Warehouse,
  Network,
  Shield
} from "lucide-react";

const API_URL = "/access/menus/";
const PRIVILEGES_API_URL = "/access/privileges/";

// Icon dictionary for permission access
const iconDictionary = {
  Dashboard: <LayoutDashboard size={20} />,
  Users: <Users size={20} />,
  Calendar: <Calendar size={20} />,
  Tasks: <FileText size={20} />,
  Reports: <FileBarChart size={20} />,
  Finance: <Banknote size={20} />,
  Tickets: <Folder size={20} />,
  Meetings: <CalendarClock size={20} />,
  Settings: <Settings size={20} />,
  Hardware: <HardDrive size={20} />,
  Categories: <Layers size={20} />,
  Departments: <Building size={20} />,
  Projects: <Folder size={20} />,
  Documents: <FileText size={20} />,
  Inventory: <Package size={20} />,
  Locations: <Warehouse size={20} />,
  Connections: <Network size={20} />,
  Security: <Shield size={20} />
};

// Generate icon options for react-select using the above dictionary
const iconOptions = Object.keys(iconDictionary).map((key) => ({
  value: key,
  label: key,
  icon: iconDictionary[key]
}));

const ModuleAccess = () => {
  const [permissions, setPermissions] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedPrivileges, setSelectedPrivileges] = useState([]);
  const toast = useRef(null);

  // Form state for permission add/edit
  const [permissionForm, setPermissionForm] = useState({
    name: "",
    path: "",
    icon: null,
    privileges: [],
    status_db: 1
  });

  // Pagination change handler
  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  // Fetch permissions from API
  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await getRequest(API_URL);
      if (response) {
        setPermissions(response);
        if (response.count) setTotalRecords(response.count);
      }
    } catch (error) {
      showToast("error", "Error", "Failed to fetch permissions");
      console.error("Error fetching permissions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch privileges from API
  const fetchPrivileges = async () => {
    try {
      const response = await getRequest(PRIVILEGES_API_URL);
      if (response) {
        setPrivileges(response);
      }
    } catch (error) {
      showToast("error", "Error", "Failed to fetch privileges");
      console.error("Error fetching privileges:", error);
    }
  };

  useEffect(() => {
    fetchPermissions();
    fetchPrivileges();
  }, []);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  // Open Add Dialog
  const openAddDialog = () => {
    setIsEditing(false);
    setPermissionForm({
      name: "",
      path: "",
      icon: null,
      privileges: [],
      status_db: 1
    });
    setSelectedPrivileges([]);
    setSelectedIcon(null);
    setShowPermissionDialog(true);
  };

  // Open Edit Dialog
  const openEditDialog = (permission) => {
    setIsEditing(true);
    setPermissionForm({
      id: permission.id,
      name: permission.name,
      path: permission.path,
      icon: permission.icon,
      privileges: permission.privileges || [],
      status_db: permission.status_db || 1
    });
    setSelectedPrivileges(permission.privileges || []);
    // Find the matching icon option
    const iconOption = iconOptions.find(option => option.value === permission.icon);
    setSelectedIcon(iconOption || null);
    setShowPermissionDialog(true);
  };

  // Confirm Delete Dialog
  const confirmDeletePermission = (permission) => {
    setSelectedPermission(permission);
    setShowDeleteDialog(true);
  };

  // Delete Permission
  const deletePermission = async () => {
    try {
      await deleteRequest(`${API_URL}${selectedPermission.id}/`);
      showToast("success", "Success", "Permission deleted successfully");
      fetchPermissions();
    } catch (error) {
      showToast("error", "Error", "Failed to delete permission");
      console.error("Error deleting permission:", error);
    }
    setShowDeleteDialog(false);
    setSelectedPermission(null);
  };

  // Save Permission (Add/Edit)
  const savePermission = async () => {
    if (!permissionForm.name || !permissionForm.path) {
      showToast("error", "Error", "Name and Path are required");
      return;
    }

    if (!selectedIcon) {
      showToast("error", "Error", "Icon selection is required");
      return;
    }

    const payload = {
      ...permissionForm,
      icon: selectedIcon.value,
      privileges: selectedPrivileges
    };

    try {
      if (isEditing) {
        await putRequest(`${API_URL}${permissionForm.id}/`, payload);
        showToast("success", "Success", "Permission updated successfully");
      } else {
        await postRequest(API_URL, payload);
        showToast("success", "Success", "Permission added successfully");
      }
      fetchPermissions();
      setShowPermissionDialog(false);
    } catch (error) {
      showToast("error", "Error", "Failed to save permission");
      console.error("Error saving permission:", error);
    }
  };

  // Handle privilege selection
  const onPrivilegeChange = (e) => {
    let selected = [...selectedPrivileges];
    if (e.checked) {
      selected.push(e.value);
    } else {
      selected = selected.filter((item) => item !== e.value);
    }
    setSelectedPrivileges(selected);
  };

  // Table body templates
  const nameBodyTemplate = (rowData) => (
    <div className="font-medium">{rowData.name}</div>
  );

  const pathBodyTemplate = (rowData) => (
    <div className="text-sm text-gray-600 font-mono">{rowData.path}</div>
  );

  const iconBodyTemplate = (rowData) => {
    const Icon = iconDictionary[rowData.icon];
    return (
      <div className="flex justify-center">
        {Icon || <i className={rowData.icon}></i>}
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => (
    <div className="flex justify-center">
      {rowData.status_db === 1 ? (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <i className="pi pi-check-circle mr-1"></i> Active
        </span>
      ) : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <i className="pi pi-times-circle mr-1"></i> Inactive
        </span>
      )}
    </div>
  );

  const actionBodyTemplate = (rowData) => (
    <div className="flex justify-center gap-2">
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-outlined p-button-warning"
        onClick={() => openEditDialog(rowData)}
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-outlined p-button-danger"
        onClick={() => confirmDeletePermission(rowData)}
        tooltip="Delete"
        tooltipOptions={{ position: "top" }}
      />
    </div>
  );

  // Define columns for the table
  const columns = [
    { field: "name", header: "Name", sortable: true, body: nameBodyTemplate, headerStyle: { backgroundColor: '#f0f9ff' } },
    { field: "path", header: "Path", sortable: true, body: pathBodyTemplate, headerStyle: { backgroundColor: '#f0f9ff' } },
    { field: "icon", header: "Icon", body: iconBodyTemplate, headerStyle: { backgroundColor: '#f0f9ff' } },
    { field: "status_db", header: "Status", body: statusBodyTemplate, headerStyle: { backgroundColor: '#f0f9ff' } },
    { header: "Actions", body: actionBodyTemplate, headerStyle: { backgroundColor: '#f0f9ff' } }
  ];

  // Define action items for row action menu
  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditDialog(rowData)
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => confirmDeletePermission(rowData)
    }
  ];

  const customDialogFooter = (
    <div className="flex justify-end gap-2 pt-4 border-t mt-4">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setShowPermissionDialog(false)}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-primary"
        onClick={savePermission}
      />
    </div>
  );

  const deleteDialogFooter = (
    <div className="flex justify-end gap-2 pt-4 border-t mt-4">
      <Button
        label="No"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setShowDeleteDialog(false)}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        className="p-button-danger"
        onClick={deletePermission}
      />
    </div>
  );

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <Toast ref={toast} position="top-right" />

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Module Access</h1>
            {/* <p className="text-gray-600 mt-1">Manage system permissions and module access</p> */}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
                placeholder="Search privileges..."
                value={globalFilter || ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
            <button
              onClick={openAddDialog}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-white px-4 py-2.5 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add New</span>
            </button>
          </div>
        </div>

        <div className="overflow-hidden">
          <ReusableTable
            data={permissions}
            setData={setPermissions}
            columnFields={columns}
            // setAddNewDialogVisible={openAddDialog}
            actionMenu={true}
            actionItems={actionItems}
            loading={loading}
            totalRecords={totalRecords}
            rows={rowsPerPage}
            onPageChange={onPageChange}
            first={(currentPage - 1) * rowsPerPage}
            globalFilter={globalFilter}
            emptyMessage="No permissions found"
            className="p-datatable-sm"
            stripedRows
            responsiveLayout="stack"
            breakpoint="960px"
            colorScheme={{
              headerBgColor: '#1e40af',
              headerTextColor: '#ffffff',
              tableBgColor: '#f3f4f6',
              tableTextColor: '#1f2937',
            }}
          />
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog
        visible={showPermissionDialog}
        className="w-11/12 max-w-3xl p-fluid"
        header={isEditing ? "Edit Permission" : "Add Permission"}
        modal
        footer={customDialogFooter}
        onHide={() => setShowPermissionDialog(false)}
        closeOnEscape
        draggable={false}
        resizable={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          <div className="field col-span-1 md:col-span-2">
            <label htmlFor="name" className="font-medium text-gray-700 block mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <InputText
              id="name"
              value={permissionForm.name}
              onChange={(e) =>
                setPermissionForm({ ...permissionForm, name: e.target.value })
              }
              required
              autoFocus
              className="w-full p-inputtext-sm"
              placeholder="Enter permission name"
            />
          </div>

          <div className="field col-span-1 md:col-span-2">
            <label htmlFor="path" className="font-medium text-gray-700 block mb-2">
              Path <span className="text-red-500">*</span>
            </label>
            <InputText
              id="path"
              value={permissionForm.path}
              onChange={(e) =>
                setPermissionForm({ ...permissionForm, path: e.target.value })
              }
              required
              className="w-full p-inputtext-sm"
              placeholder="/route/path"
            />
            <small className="text-gray-500">Application route for this permission</small>
          </div>

          <div className="field col-span-1 md:col-span-2">
            <label className="font-medium text-gray-700 block mb-2">
              Select Icon <span className="text-red-500">*</span>
            </label>
            <Select
              options={iconOptions}
              value={selectedIcon}
              getOptionLabel={(e) => (
                <div className="flex items-center">
                  {e.icon} <span className="ml-2">{e.label}</span>
                </div>
              )}
              onChange={(option) => setSelectedIcon(option)}
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Select an icon"
              isSearchable
            />
          </div>

          <div className="field col-span-1 md:col-span-2">
            <label className="font-medium text-gray-700 block mb-3">
              Select Privileges <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 bg-gray-50 p-3 rounded-lg max-h-60 overflow-y-auto">
              {privileges.map((privilege) => (
                <div key={privilege.id} className="flex items-center p-2 hover:bg-gray-100 rounded">
                  <Checkbox
                    inputId={`privilege-${privilege.id}`}
                    name="privilege"
                    value={privilege.id}
                    onChange={onPrivilegeChange}
                    checked={selectedPrivileges.includes(privilege.id)}
                  />
                  <label htmlFor={`privilege-${privilege.id}`} className="ml-2 cursor-pointer">
                    {privilege.name}
                  </label>
                </div>
              ))}
            </div>
            <small className="text-gray-500 block mt-2">Specific privileges for this permission</small>
          </div>

          <div className="field col-span-1 md:col-span-2 mt-2">
            <div className="flex items-center p-3 bg-gray-50 rounded">
              <Checkbox
                inputId="status"
                checked={permissionForm.status_db === 1}
                onChange={(e) =>
                  setPermissionForm({ ...permissionForm, status_db: e.checked ? 1 : 0 })
                }
              />
              <label htmlFor="status" className="ml-2 cursor-pointer font-medium">
                Active
              </label>
            </div>
            <small className="text-gray-500 block mt-1">Inactive permissions won't be accessible</small>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={showDeleteDialog}
        className="max-w-md delete-dialog"
        header="Confirm Deletion"
        modal
        footer={deleteDialogFooter}
        onHide={() => setShowDeleteDialog(false)}
        closeOnEscape
        draggable={false}
        resizable={false}
      >
        <div className="flex items-center justify-center py-4">
          <i
            className="pi pi-exclamation-triangle mr-3 text-yellow-500 text-4xl"
          />
          <div>
            {selectedPermission && (
              <div>
                <p className="mb-2">Are you sure you want to delete this permission?</p>
                <p className="font-bold text-lg">{selectedPermission.name}</p>
                <p className="text-gray-500 text-sm mt-2">This action cannot be undone.</p>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ModuleAccess;